import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, User, Save, Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerSettings = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Form data
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    birthDate: '',
    gender: ''
  });

  // Password change
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const auth = localStorage.getItem('customerData');
    if (!auth) {
      navigate('/cliente/login');
      return;
    }
    const customerData = JSON.parse(auth);
    setCustomer(customerData);
    setFormData({
      fullName: customerData.full_name || '',
      email: customerData.email || '',
      phone: customerData.phone || '',
      birthDate: customerData.birth_date || '',
      gender: customerData.gender || ''
    });
  }, [navigate]);

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      await axios.put(`${BACKEND_URL}/api/customers/me/${customer.cpf}`, {
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        birth_date: formData.birthDate,
        gender: formData.gender || null
      });

      // Atualizar localStorage
      const updatedCustomer = {
        ...customer,
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        birth_date: formData.birthDate,
        gender: formData.gender
      };
      localStorage.setItem('customerData', JSON.stringify(updatedCustomer));
      setCustomer(updatedCustomer);

      setMessage({ type: 'success', text: 'Dados atualizados com sucesso!' });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao atualizar dados';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword) {
      setPasswordMessage({ type: 'error', text: 'Digite a senha atual' });
      return;
    }
    if (!passwordData.newPassword) {
      setPasswordMessage({ type: 'error', text: 'Digite a nova senha' });
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'A nova senha deve ter pelo menos 6 caracteres' });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'As senhas não conferem' });
      return;
    }

    try {
      setLoading(true);
      setPasswordMessage({ type: '', text: '' });

      await axios.post(`${BACKEND_URL}/api/customers/change-password/${customer.cpf}`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });

      setPasswordMessage({ type: 'success', text: 'Senha alterada com sucesso!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao alterar senha';
      setPasswordMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  if (!customer) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#FFD700]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/minha-conta" className="inline-flex items-center text-[#003366] hover:underline mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar para Minha Conta
        </Link>

        <h1 className="text-2xl font-bold text-gray-900 mb-6">Configurações da Conta</h1>

        {/* Dados Pessoais */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <User className="w-5 h-5 text-[#003366]" />
              <h2 className="text-lg font-semibold text-gray-900">Dados Pessoais</h2>
            </div>

            {message.text && (
              <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                {message.text}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label>CPF (não editável)</Label>
                <Input 
                  value={customer.cpf?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')} 
                  disabled 
                  className="mt-1 bg-gray-100" 
                />
              </div>
              <div>
                <Label>Nome Completo</Label>
                <Input
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>E-mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Data de Nascimento</Label>
                <Input
                  value={formData.birthDate}
                  onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  placeholder="DD/MM/AAAA"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Sexo</Label>
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 mt-1"
                >
                  <option value="">Não informado</option>
                  <option value="masculino">Masculino</option>
                  <option value="feminino">Feminino</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
              <Button onClick={handleSaveProfile} disabled={loading} className="bg-[#003366] hover:bg-[#002244]">
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Alterar Senha */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Lock className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-semibold text-gray-900">Alterar Senha</h2>
            </div>

            {passwordMessage.text && (
              <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${passwordMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                {passwordMessage.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                {passwordMessage.text}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label>Senha Atual</Label>
                <div className="relative mt-1">
                  <Input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    placeholder="Digite sua senha atual"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Nova Senha</Label>
                <div className="relative mt-1">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    placeholder="Mínimo 6 caracteres"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Confirmar Nova Senha</Label>
                <Input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Repita a nova senha"
                  className="mt-1"
                />
              </div>
              <Button onClick={handleChangePassword} disabled={loading} className="bg-red-600 hover:bg-red-700">
                <Lock className="w-4 h-4 mr-2" />
                {loading ? 'Alterando...' : 'Alterar Senha'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerSettings;
